export interface Position {
  coords: {
    latitude: number;
    longitude: number;
    accuracy?: number;
    altitude?: number | null;
    altitudeAccuracy?: number | null;
    heading?: number | null;
    speed?: number | null;
  };
  timestamp: number;
}

export interface PositionError {
  code: number;
  message: string;
}

export interface PositionOptions {
  enableHighAccuracy?: boolean;
  timeout?: number;
  maximumAge?: number;
}

export const getCurrentPosition = (
  successCallback: (position: Position) => void,
  errorCallback?: (error: PositionError) => void,
  options?: PositionOptions
): void => {
  if (!navigator.geolocation) {
    if (errorCallback) {
      errorCallback({
        code: 0,
        message: 'Geolocation is not supported by this browser.',
      });
    }
    return;
  }

  const defaultOptions: PositionOptions = {
    enableHighAccuracy: true,
    timeout: 15000,
    maximumAge: 0,
  };

  navigator.geolocation.getCurrentPosition(
    (position) => {
      successCallback({
        coords: {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          altitude: position.coords.altitude,
          altitudeAccuracy: position.coords.altitudeAccuracy,
          heading: position.coords.heading,
          speed: position.coords.speed,
        },
        timestamp: position.timestamp,
      });
    },
    (error) => {
      if (errorCallback) {
        errorCallback({
          code: error.code,
          message: error.message,
        });
      }
    },
    { ...defaultOptions, ...options }
  );
};

export const watchPosition = (
  successCallback: (position: Position) => void,
  errorCallback?: (error: PositionError) => void,
  options?: PositionOptions
): number => {
  if (!navigator.geolocation) {
    if (errorCallback) {
      errorCallback({
        code: 0,
        message: 'Geolocation is not supported by this browser.',
      });
    }
    return -1;
  }

  const defaultOptions: PositionOptions = {
    enableHighAccuracy: true,
    timeout: 15000,
    maximumAge: 0,
  };

  return navigator.geolocation.watchPosition(
    (position) => {
      successCallback({
        coords: {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          altitude: position.coords.altitude,
          altitudeAccuracy: position.coords.altitudeAccuracy,
          heading: position.coords.heading,
          speed: position.coords.speed,
        },
        timestamp: position.timestamp,
      });
    },
    (error) => {
      if (errorCallback) {
        errorCallback({
          code: error.code,
          message: error.message,
        });
      }
    },
    { ...defaultOptions, ...options }
  );
};

export const clearWatch = (watchId: number): void => {
  navigator.geolocation.clearWatch(watchId);
};

